import { cn } from '@/_lib/utils'
import { Check, Copy } from 'lucide-react'
import * as React from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip'

const COPIED_FEEDBACK_MS = 1400

export interface CopyButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'aria-label'
> {
  value: string
  label?: string
  successLabel?: string
}

// Refined, neutral-tone copy button. Tooltip text is the primary feedback
// channel ("Copiar" → "Copiado"); the icon swap (Copy → Check) is a secondary
// cue kept in the same muted color so the button never visually "shouts".
// Controlled Tooltip open state keeps the success label visible for a moment
// even if the cursor leaves the trigger.
export const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ value, label = 'Copiar', successLabel = 'Copiado', className, ...props }, ref) => {
    const [copied, setCopied] = React.useState(false)

    React.useEffect(() => {
      if (!copied) return
      const t = window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS)
      return () => window.clearTimeout(t)
    }, [copied])

    const onCopy = async () => {
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
      } catch {
        // Clipboard API blocked (insecure context, permission denied). Fail
        // silent; user can still select-copy the underlying text manually.
      }
    }

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip open={copied ? true : undefined}>
          <TooltipTrigger asChild>
            <button
              ref={ref}
              type="button"
              onClick={onCopy}
              aria-label={copied ? successLabel : label}
              className={cn(
                'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-brand-grayscale-6 transition-colors hover:bg-brand-grayscale-3 hover:text-brand-grayscale-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                className,
              )}
              {...props}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {copied ? successLabel : label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  },
)
CopyButton.displayName = 'CopyButton'
